import {action, Action, computed, Computed, thunk, Thunk} from 'easy-peasy';
import {jsonToFormData} from '~/app/helpers/headerContentType'
import {CostCategory, Customer, SaveMaterialPayload, HPCode, Issue, IssueCategory, IssueClosePayload, IssueDetails, IssueImagePayload, IssuePayload, IssueResponsePayload, IssueSortingType, Message, WorkType, Material, DeleteMaterialPayload, SaveDeleteMaterialResponse, SearchMaterialResponse, APIResponse, Label, IssueChatPayload, SearchUser, SaveChatUserResponse, SaveChatUserPayload} from '~/types';
import {StoreModel} from "~/store";
import {API} from "~/app/routes";
import {CommonModel, fetch, fetchCancel, getCommonModel} from "~/store/commonStore";
import axios from "axios";
import {IssueOfflineService} from "~/sqlite/services/issue";
import {IssueStatuses, IssueTypes} from "~/app/data/statuses/issue";
import {isIssueFinished, matchesIssueCategory} from "~/issues/helpers";

export interface IssueTabCounts {
    open: number
    completed: number
}

export interface IssueModel extends CommonModel<Issue> {
    categories: IssueCategory[]
    fetchingIssueCategories: boolean
    setFetchingIssueCategories: Action<IssueModel, boolean>
    fetchIssueCategories: Thunk<IssueModel, void, any, StoreModel, Promise<IssueCategory[]>>
    fetchIssueCategoriesCancel: Action<IssueModel>
    fetchedIssueCategories: Action<IssueModel, IssueCategory[]>
    setOfflineIssueCategories: Thunk<IssueModel, IssueCategory[], any, StoreModel, Promise<void>>
    getOfflineIssueCategories: Thunk<IssueModel, void, any, StoreModel, Promise<void>>

    issueUpdating: boolean
    setIssueUpdating: Action<IssueModel, boolean>
    saveIssue: Thunk<IssueModel, IssuePayload, any, StoreModel, Promise<IssueResponsePayload>>

    showCompleted: boolean
    setShowCompleted: Action<IssueModel, boolean>

    categorySearchText: string
    setCategorySearchText: Action<IssueModel, string>
    isCategorySearchOpen: boolean
    setIsCategorySearchOpen: Action<IssueModel, boolean>

    filteredIssues: Computed<IssueModel, Issue[], StoreModel>
    issueCounts: Computed<IssueModel, IssueTabCounts, StoreModel>
    filteredOpenIssues: Computed<IssueModel, Issue[], StoreModel>

    data: IssueDetails,
    fetchIssue: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    fetchedIssue: Action<IssueModel, IssueDetails>
    fetchFavoriteMachinesIssues: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    addChatMessage: Action<IssueModel, Message>
    addChatUser: Thunk<IssueModel, IssueChatPayload, any, StoreModel, Promise<SaveChatUserResponse>>

    setOfflineFavoriteMachinesIssues: Thunk<IssueModel, IssueDetails[], any, StoreModel, Promise<void>>
    fetchOfflineIssue: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    setOfflineIssue: Thunk<IssueModel, IssueDetails, any, StoreModel, Promise<void>>

    saveOfflineIssue: Thunk<IssueModel, IssuePayload, any, StoreModel, Promise<IssueResponsePayload>>

    updateIssueImage: Thunk<IssueModel, IssueImagePayload, any, StoreModel, Promise<void>>

    closeIssue: Thunk<IssueModel, IssueClosePayload, any, StoreModel, Promise<APIResponse>>
    completeIssue: Thunk<IssueModel, IssueClosePayload, any, StoreModel, Promise<APIResponse>>

    issueChatUserUpdating: boolean
    setIssueChatUserUpdating: Action<IssueModel, boolean>
    tempChatUsers: SearchUser[]
    setTempChatUsers: Action<IssueModel, SearchUser[]>
    setChatUsers: Thunk<IssueModel, SearchUser[]>;
    fetchChatUsers: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    saveChatUser: Thunk<IssueModel, SaveChatUserPayload, any, StoreModel, Promise<SaveChatUserResponse>>

    materialUpdating: boolean
    setMaterialUpdating: Action<IssueModel, boolean>
    tempMaterials: Material[]
    setTempMaterials: Action<IssueModel, Material[]>
    setMaterials: Thunk<IssueModel, Material[]>;
    fetchMaterials: Thunk<IssueModel, IssueDetails['id'], any, StoreModel, Promise<void>>
    searchMaterials: Material[]
    searchMaterialsTotal: number
    setSearchedMaterials: Action<IssueModel, SearchMaterialResponse>
    fetchSearchMaterials: Thunk<IssueModel, { searchText: string, offset: number }, any, StoreModel, Promise<void>>
    saveMaterial: Thunk<IssueModel, SaveMaterialPayload, any, StoreModel, Promise<SaveDeleteMaterialResponse>>
    deleteMaterial: Thunk<IssueModel, DeleteMaterialPayload, any, StoreModel, Promise<SaveDeleteMaterialResponse>>

    isWorkOrderFormActive: boolean
    setIsWorkOrderFormActive: Action<IssueModel, boolean>

    displayCustomer: boolean
    setDisplayCustomer: Action<IssueModel, boolean>

    displayProject: boolean
    setDisplayProject: Action<IssueModel, boolean>

    sortingTypes: IssueSortingType[]
    fetchingIssueSortingTypes: boolean
    setFetchingIssueSortingTypes: Action<IssueModel, boolean>
    fetchIssueSortingTypes: Thunk<IssueModel, void, any, StoreModel, Promise<IssueSortingType[]>>
    fetchIssueSortingTypesCancel: Action<IssueModel>
    fetchedIssueSortingTypes: Action<IssueModel, IssueSortingType[]>
    setOfflineIssueSortingTypes: Thunk<IssueModel, IssueSortingType[], any, StoreModel, Promise<void>>
    getOfflineIssueSortingTypes: Thunk<IssueModel, void, any, StoreModel, Promise<void>>

    labels: Label[]
    fetchingLabels: boolean
    setFetchingLabels: Action<IssueModel, boolean>
    fetchLabels: Thunk<IssueModel, void, any, StoreModel, Promise<Label[]>>
    fetchLabelsCancel: Action<IssueModel>
    fetchedLabels: Action<IssueModel, Label[]>
    setOfflineLabels: Thunk<IssueModel, Label[], any, StoreModel, Promise<void>>
    getOfflineLabels: Thunk<IssueModel, void, any, StoreModel, Promise<void>>

    HPCodes: HPCode[]
    fetchingHPCodes: boolean
    setFetchingHPCodes: Action<IssueModel, boolean>
    fetchHPCodes: Thunk<IssueModel, void, any, StoreModel, Promise<HPCode[]>>
    fetchHPCodesCancel: Action<IssueModel>
    fetchedHPCodes: Action<IssueModel, HPCode[]>

    isHPCodeRequired: boolean
    setIsHPCodeRequired: Action<IssueModel, boolean>

    workTypes: WorkType[]
    fetchingWorkTypes: boolean
    setFetchingWorkTypes: Action<IssueModel, boolean>
    fetchWorkTypes: Thunk<IssueModel, void, any, StoreModel, Promise<WorkType[]>>
    fetchWorkTypesCancel: Action<IssueModel>
    fetchedWorkTypes: Action<IssueModel, WorkType[]>

    costCategories: CostCategory[]
    fetchingCostCategories: boolean
    setFetchingCostCategories: Action<IssueModel, boolean>
    fetchCostCategories: Thunk<IssueModel, IssueDetails['departmentId'], any, StoreModel, Promise<CostCategory[]>>
    fetchCostCategoriesCancel: Action<IssueModel>
    fetchedCostCategories: Action<IssueModel, CostCategory[]>

    customers: Customer[]
    fetchingCustomers: boolean
    setFetchingCustomers: Action<IssueModel, boolean>
    fetchCustomers: Thunk<IssueModel, void, any, StoreModel, Promise<Customer[]>>
    fetchCustomersCancel: Action<IssueModel>
    fetchedCustomers: Action<IssueModel, Customer[]>

    isReadOnlyChat: Computed<IssueModel, boolean, StoreModel>
}

export const issueModel: IssueModel = {
    ...getCommonModel<Issue>(API.issue),

    categories: [],
    fetchingIssueCategories: false,
    setFetchingIssueCategories: action((state, payload) => {
        state.fetchingIssueCategories = payload;
    }),
    fetchIssueCategories: thunk((actions) => {
        actions.setFetchingIssueCategories(true);
        return fetch<IssueCategory>(`${API.issue}/categories`)
            .then(data => {
                actions.fetchedIssueCategories(data);
                actions.setOfflineIssueCategories(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingIssueCategories(false);
            });
    }),
    fetchIssueCategoriesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedIssueCategories: action((state, payload) => {
        state.categories = payload;
    }),
    setOfflineIssueCategories: thunk(async (actions, payload) => {
        const IssueOffline = new IssueOfflineService();
        await IssueOffline.resetIssueCategories(payload);
    }),
    getOfflineIssueCategories: thunk(async (actions) => {
        const IssueOffline = new IssueOfflineService();
        let issueCategories = await IssueOffline.getAllIssueCategories();
        issueCategories && actions.fetchedIssueCategories(issueCategories);
    }),

    issueUpdating: false,
    setIssueUpdating: action((state, payload) => {
        state.issueUpdating = payload;
    }),
    saveIssue: thunk((actions, payload) => {
        actions.setIssueUpdating(true);
        return axios.post<null, IssueResponsePayload>(`${API.issue}/save`, jsonToFormData(payload)).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setIssueUpdating(false);
        })
    }),

    showCompleted: false,
    setShowCompleted: action((state, payload) => {
        state.showCompleted = payload;
    }),

    categorySearchText: '',
    setCategorySearchText: action((state, payload) => {
        state.categorySearchText = payload;
    }),
    isCategorySearchOpen: false,
    setIsCategorySearchOpen: action((state, payload) => {
        state.isCategorySearchOpen = payload;
    }),

    filteredIssues: computed(
        [
            state => state.showCompleted,
            state => state.categorySearchText,
            (state, storeState) => storeState.machine.data.issues
        ],
        (status, searchText, issues) => issues.filter(issue =>
            (status ? isIssueFinished(issue) : !isIssueFinished(issue)) && matchesIssueCategory(issue.categoryName, searchText)
        )
    ),
    issueCounts: computed(
        [
            state => state.categorySearchText,
            (state, storeState) => storeState.machine.data.issues
        ],
        (searchText, issues) => issues.reduce((counts, issue) => {
            if (!matchesIssueCategory(issue.categoryName, searchText)) {
                return counts;
            }
            if (isIssueFinished(issue)) {
                counts.completed++;
            } else {
                counts.open++;
            }
            return counts;
        }, { open: 0, completed: 0 })
    ),
    filteredOpenIssues: computed(
        [
            (state, storeState) => storeState.machine.data.issues
        ],
        (issues) => issues.filter(issue => !(issue.status === IssueStatuses.COMPLETED || issue.status === IssueStatuses.CLOSED) && [IssueTypes.CRITICAL, IssueTypes.MORECRITICAL].includes(issue.type))
    ),

    data: {
        id: '',
        type: 0,
        description: '',
        status: 0,
        statusDate: '',
        comment: '',
        seenDate: '',
        images: [],
        files: [],
        categoryId: '',
        categoryName: '',
        issueNo: '',
        machineId: '',
        machineIntern: '',
        machineName: '',
        departmentName: '',
        departmentId: '',
        issueDepartmentName: '',
        departmentPrefix: null,
        issueDepartmentId: '',
        workOrderId: '',
        workOrderNo: '',
        workDetails: [],
        workOrderCost: { overtime1Percentage: null, overtime2Percentage: null },
        chat: [],
        chatUsers: [],
        createdAt: '',
        updatedAt: '',
        createdBy: '',
        closedAt: null,
        closedBy: null,
        issueCreatedUserId: null,
        issueCreatedBy: null,
        issueUpdatedBy: null,
        projectId: null,
        registrationDate: '',
        materials: [],
        labels: ''
    },
    fetchIssue: thunk((actions, payload, {getStoreActions}) => {
        return axios.get<IssueDetails, IssueDetails>(`${API.issue}/${payload}`)
            .then(data => {
                actions.fetchedIssue(data);
                actions.setOfflineIssue(data);
            });
    }),
    fetchedIssue: action((state, payload) => {
        state.data = payload;
    }),
    fetchFavoriteMachinesIssues: thunk((actions, payload) => {
        return axios.get<IssueDetails, IssueDetails[]>(`${API.issue}/${payload}`)
            .then(data => {
                actions.setOfflineFavoriteMachinesIssues(data);
            });
    }),

    setOfflineFavoriteMachinesIssues: thunk(async (actions, payload) => {
        const IssueOffline = new IssueOfflineService();
        /*await*/ IssueOffline.reset(payload);
    }),
    fetchOfflineIssue: thunk(async (actions, payload) => {
        const IssueOffline = new IssueOfflineService();
        let issue = await IssueOffline.getIssue(payload);
        issue && actions.fetchedIssue(issue);
    }),
    setOfflineIssue: thunk(async (actions, payload) => {
        const IssueOffline = new IssueOfflineService();
        await IssueOffline.updateIssueDetail(payload);
    }),

    saveOfflineIssue: thunk(async (actions, payload) => {
        const IssueOffline = new IssueOfflineService();
        const response = await IssueOffline.saveIssue(payload);
        return response;
    }),

    updateIssueImage: thunk((actions, payload) => {
        actions.setIssueUpdating(true);
        return axios.post<null, void>(`${API.issue}/update-issue-image`, jsonToFormData(payload))
            .then(data => data)
            .finally(() => actions.setIssueUpdating(false))
    }),

    closeIssue: thunk((actions, payload) => {
        actions.setIssueUpdating(true);
        return axios.post<null, APIResponse>(`${API.issue}/close`, jsonToFormData(payload)).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setIssueUpdating(false);
        })
    }),
    completeIssue: thunk((actions, payload) => {
        actions.setIssueUpdating(true);
        return axios.post<null, APIResponse>(`${API.issue}/complete`, jsonToFormData(payload)).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setIssueUpdating(false);
        })
    }),

    issueChatUserUpdating: false,
    setIssueChatUserUpdating: action((state, payload) => {
        state.issueChatUserUpdating = payload;
    }),
    tempChatUsers: [],
    setTempChatUsers: action((state, payload) => {
      state.tempChatUsers = payload;
    }),
    setChatUsers: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.chatUsers = payload;
        state.tempChatUsers = payload;
        await actions.setOfflineIssue(state.data);
    }),
    fetchChatUsers: thunk((actions, payload) => {
        if (String(payload ?? '').trim() === '') {
            return Promise.resolve();
        }
        return axios.post<null, SearchUser[]>(`${API.issueUsers}/fetch/${payload}`)
            .then(
                data => {
                    actions.setChatUsers(data);
                })
    }),
    saveChatUser: thunk((actions, payload) => {
        actions.setIssueChatUserUpdating(true);
        return axios.post<null, SaveChatUserResponse>(`${API.issueUsers}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setIssueChatUserUpdating(false);
        })
    }),
    addChatUser: thunk((actions, payload) => {
        return axios.post<null, SaveChatUserResponse>(`${API.issueUsers}/add`, payload).then(data => { return data; })
    }),
    addChatMessage: action((state, payload) => {
        Number(state.data.id) === Number(payload.issueId) && state.data.chat.push(payload);
    }),

    materialUpdating: false,
    setMaterialUpdating: action((state, payload) => {
        state.materialUpdating = payload;
    }),
    tempMaterials: [],
    setTempMaterials: action((state, payload) => {
        state.tempMaterials = payload;
    }),
    setMaterials: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.materials = payload;
        state.tempMaterials = payload;
        await actions.setOfflineIssue(state.data);
    }),
    fetchMaterials: thunk((actions, payload) => {
        return axios.post<null, Material[]>(`${API.issueMaterial}/fetch/${payload}`)
            .then(
                data => {
                    actions.setMaterials(data);
                })
    }),
    searchMaterials: [],
    searchMaterialsTotal: 0,
    setSearchedMaterials: action((state, payload) => {
        state.searchMaterials = payload.materials;
        state.searchMaterialsTotal = payload.total;
    }),
    fetchSearchMaterials: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SearchMaterialResponse>(`${API.issueMaterial}/search`, payload).then(
            data => {
                actions.setSearchedMaterials(data);
            }
        ).finally(() => {
            actions.setMaterialUpdating(false);
        })
    }),
    saveMaterial: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SaveDeleteMaterialResponse>(`${API.issueMaterial}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setMaterialUpdating(false);
        })
    }),
    deleteMaterial: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SaveDeleteMaterialResponse>(`${API.issueMaterial}/delete`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setMaterialUpdating(false);
        })
    }),

    isWorkOrderFormActive: false,
    setIsWorkOrderFormActive: action((state, payload) => {
        state.isWorkOrderFormActive = payload;
    }),

    displayCustomer: false,
    setDisplayCustomer: action((state, payload) => {
        state.displayCustomer = payload;
    }),

    displayProject: false,
    setDisplayProject: action((state, payload) => {
        state.displayProject = payload;
    }),

    sortingTypes: [],
    fetchingIssueSortingTypes: false,
    setFetchingIssueSortingTypes: action((state, payload) => {
        state.fetchingIssueSortingTypes = payload;
    }),
    fetchIssueSortingTypes: thunk((actions) => {
        actions.setFetchingIssueSortingTypes(true);
        return fetch<IssueSortingType>(`${API.issue}/sorting-types`)
            .then(data => {
                actions.fetchedIssueSortingTypes(data);
                actions.setOfflineIssueSortingTypes(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingIssueSortingTypes(false);
            });
    }),
    fetchIssueSortingTypesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedIssueSortingTypes: action((state, payload) => {
        state.sortingTypes = payload;
    }),
    setOfflineIssueSortingTypes: thunk(async (actions, payload) => {
        const IssueOffline = new IssueOfflineService();
        await IssueOffline.resetIssueSortingTypes(payload);
    }),
    getOfflineIssueSortingTypes: thunk(async (actions) => {
        const IssueOffline = new IssueOfflineService();
        let issueSortingTypes = await IssueOffline.getAllIssueSortingTypes();
        issueSortingTypes && actions.fetchedIssueSortingTypes(issueSortingTypes);
    }),

    HPCodes: [],
    fetchingHPCodes: false,
    setFetchingHPCodes: action((state, payload) => {
        state.fetchingHPCodes = payload;
    }),
    fetchHPCodes: thunk((actions) => {
        actions.setFetchingHPCodes(true);
        return fetch<HPCode>(`${API.workOrder}/HP-codes`)
            .then(data => {
                actions.fetchedHPCodes(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingHPCodes(false);
            });
    }),
    fetchHPCodesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedHPCodes: action((state, payload) => {
        state.HPCodes = payload;
    }),
    isHPCodeRequired: false,
    setIsHPCodeRequired: action((state, payload) => {
        state.isHPCodeRequired = payload;
    }),

    labels: [],
    fetchingLabels: false,
    setFetchingLabels: action((state, payload) => {
        state.fetchingLabels = payload;
    }),
    fetchLabels: thunk((actions) => {
        actions.setFetchingHPCodes(true);
        return fetch<Label>(`${API.issue}/get_labels`)
            .then(data => {
                actions.fetchedLabels(data);
                actions.setOfflineLabels(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingLabels(false);
            });
    }),
    fetchLabelsCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedLabels: action((state, payload) => {
        state.labels = payload;
    }),
    setOfflineLabels: thunk(async (actions, payload) => {
        const IssueOffline = new IssueOfflineService();
        await IssueOffline.resetIssueLabels(payload);
    }),
    getOfflineLabels: thunk(async (actions) => {
        const IssueOffline = new IssueOfflineService();
        let labels = await IssueOffline.getAllIssueLabels();
        labels && actions.fetchedLabels(labels);
    }),

    workTypes: [],
    fetchingWorkTypes: false,
    setFetchingWorkTypes: action((state, payload) => {
        state.fetchingWorkTypes = payload;
    }),
    fetchWorkTypes: thunk((actions) => {
        actions.setFetchingWorkTypes(true);
        return fetch<WorkType>(`${API.workOrder}/work-types`)
            .then(data => {
                actions.fetchedWorkTypes(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingWorkTypes(false);
            });
    }),
    fetchWorkTypesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedWorkTypes: action((state, payload) => {
        state.workTypes = payload;
    }),

    costCategories: [],
    fetchingCostCategories: false,
    setFetchingCostCategories: action((state, payload) => {
        state.fetchingCostCategories = payload;
    }),
    fetchCostCategories: thunk((actions, payload) => {
        actions.setFetchingCostCategories(true);
        return fetch<CostCategory>(`${API.workOrder}/cost-category-list-by-department/${payload}`)
            .then(data => {
                actions.fetchedCostCategories(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingCostCategories(false);
            });
    }),
    fetchCostCategoriesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedCostCategories: action((state, payload) => {
        state.costCategories = payload;
    }),

    customers: [],
    fetchingCustomers: false,
    setFetchingCustomers: action((state, payload) => {
        state.fetchingCustomers = payload;
    }),
    fetchCustomers: thunk((actions, payload) => {
        actions.setFetchingCustomers(true);
        return fetch<Customer>(`${API.workOrder}/customers`)
            .then(data => {
                actions.fetchedCustomers(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingCustomers(false);
            });
    }),
    fetchCustomersCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedCustomers: action((state, payload) => {
        state.customers = payload;
    }),

    isReadOnlyChat: computed(
        [state => state.data.status],
        (status) => (status === IssueStatuses.COMPLETED || status === IssueStatuses.CLOSED)
    ),
};
